import { faEllipsisV, faSync, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { memo, useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import FeedbackCard from '@/components/FeedbackCard';
import SendScheduleSummaryButton from '@/components/SendScheduleSummaryButton';
import { defaultUpcomingAppointmentAttendanceStates } from '@/constants/appointmentAttendance';
import { Color } from '@/constants/color';
import defaultTimeZone from '@/constants/defaultTimeZone';
import { STICKY_CHROME_CLASS } from '@/constants/layout';
import AppContext from '@/contexts/appContext';
import AppointmentsFilterForm from '@/forms/AppointmentsFilterForm';
import useAPI from '@/hooks/useAPI';
import useNavigateToPage from '@/hooks/useNavigateToPage';
import usePagination from '@/hooks/usePagination';
import PersonContext from '@/pages/Person/PersonContext';
import AppointmentAttendanceInsights from '@/pages/PersonAppointments/components/AppointmentAttendanceInsights';
import AppointmentsRepository, {
  type GetAppointmentsParams
} from '@/repositories/AppointmentsRepository';
import PersonRepository from '@/repositories/PersonRepository';
import type { AppointmentAttendance } from '@/types/appointments';
import AppointmentAttendanceCard from '@/ui/components/AppointmentAttendanceCard';
import Button from '@/ui/components/Button';
import Container from '@/ui/components/Container';
import Dropdown from '@/ui/components/Dropdown';
import Heading from '@/ui/components/Heading';
import LoadingSkeleton from '@/ui/components/LoadingSkeleton';
import Paginator from '@/ui/components/Paginator';
import Spinner from '@/ui/components/Spinner';
import formatPersonName from '@/utils/formatPersonName';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';
import {
  APPOINTMENTS_BOOKING_UI,
  hasPermission,
  PEOPLE_VIEW_ENCOUNTERS
} from '@/utils/userPermission';

import { AppointmentsSegment } from './types';

const PersonAppointments = () => {
  const { addMessage, permissions } = useContext(AppContext);
  const {
    person,
    person: { external_links }
  } = useContext(PersonContext);
  const apptRepo = AppointmentsRepository();
  const personRepository = PersonRepository();
  const [searchParams, setSearchParams] = useSearchParams({
    state__in: defaultUpcomingAppointmentAttendanceStates.join(',')
  });

  // Defaults live only in the hook until written to the URL, so clearing an
  // already-empty URL would otherwise leave them applied.
  useEffect(() => {
    setSearchParams((currentParams) => currentParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const { currentPage, goToPage, nextPage, prevPage } = usePagination(page, totalPages);

  const [segment, setSegment] = useState<AppointmentsSegment>(AppointmentsSegment.UPCOMING);
  const segmentOrderBy =
    segment === AppointmentsSegment.PAST ? '-appointment__start_at' : 'appointment__start_at';

  const { navigateToBooking } = useNavigateToPage();

  // Encounter routes are only registered for users with this permission.
  const canViewEncounters = hasPermission({
    requiredPermission: PEOPLE_VIEW_ENCOUNTERS,
    permissions
  });

  useEffect(() => {
    setPage(1); // Prevent fetching invalid page
  }, [searchParams]);

  useEffect(() => {
    setPage(currentPage);
  }, [currentPage]);

  const {
    isError,
    isPending: isLoadingAppointments,
    isSuccess,
    data: appointmentsResponse
  } = useAPI({
    queryKey: ['getAppointments', person?.id, segment, page, Object.fromEntries(searchParams)],
    queryFn: async () => {
      const params: Partial<GetAppointmentsParams> = {
        ...Object.fromEntries(searchParams),
        person_id: person?.id as string,
        order_by: segmentOrderBy,
        page,
        [segment]: true
      };

      return await apptRepo.getAppointments(params);
    }
  });

  useEffect(() => {
    if (isSuccess) {
      setPage(appointmentsResponse.data?.page ?? 1);

      if (appointmentsResponse?.data?.total_pages) {
        setTotalPages(appointmentsResponse.data.total_pages);
      }
    }
  }, [
    appointmentsResponse?.data?.page,
    appointmentsResponse?.data?.total_pages,
    isSuccess,
    setSearchParams
  ]);

  const handleTabs = (nextSegment: AppointmentsSegment) => {
    if (nextSegment !== segment) {
      // Replaces rather than pushes: the tab lives in local state, so Back
      // would restore one tab's statuses while the other tab stays selected.
      setSearchParams(
        (currentParams) => {
          if (nextSegment === AppointmentsSegment.UPCOMING) {
            currentParams.set('state__in', defaultUpcomingAppointmentAttendanceStates.join(','));
          } else {
            currentParams.delete('state__in');
          }
          return currentParams;
        },
        { replace: true }
      );
    }
    setSegment(nextSegment);
    goToPage(1);
  };

  const deleteFutureAppointments = useMutation({
    mutationFn: async () => {
      return await personRepository.deleteFutureAppointments(person.id);
    },
    onSuccess: () => {
      addMessage({
        level: 'info',
        text: 'Delete future appointments worker queued successfully.'
      });
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const { mutate: fetchUpcomingAppointments, isPending: isLoadingFetchUpcoming } = useMutation({
    mutationFn: async () => {
      return await personRepository.fetchUpcomingAppointments(person.id);
    },
    onSuccess: () => {
      addMessage({
        level: 'info',
        text: 'Fetch upcoming appointments queued successfully.'
      });
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const handleDeleteFutureAppointments = () =>
    confirm('Are you sure you want to delete all upcoming appointments for this person?') &&
    deleteFutureAppointments.mutate();

  const renderAppointmentsDropdown = () => {
    const items = [
      <button key="fetch-upcoming-appointments" onClick={() => fetchUpcomingAppointments()}>
        <FontAwesomeIcon icon={faSync} className="mr-2" />
        Fetch Upcoming Appointments
        {isLoadingFetchUpcoming && <Spinner />}
      </button>
    ];

    if (segment === AppointmentsSegment.UPCOMING && appointmentsResponse?.data?.results.length) {
      items.push(
        <button key="delete-future-appointments" onClick={handleDeleteFutureAppointments}>
          <FontAwesomeIcon icon={faTrash} className="mr-2 text-red-600" />
          Delete Upcoming Appointments
        </button>
      );
    }

    return (
      <Dropdown
        buttonText={<FontAwesomeIcon icon={faEllipsisV} />}
        items={items}
        showChevronIcon={false}
      />
    );
  };

  const renderSegment = () => {
    switch (segment) {
      case AppointmentsSegment.UPCOMING:
      case AppointmentsSegment.PAST:
        return (
          <>
            {isLoadingAppointments && (
              <LoadingSkeleton spinnerProps={{ label: `Loading ${segment} appointments...` }} />
            )}

            {isError && (
              <div className="flex items-center p-3">
                <span className="text-red-500">Error loading appointments</span>
              </div>
            )}

            {!isLoadingAppointments && !appointmentsResponse?.data?.results.length ? (
              <div className="text-center">
                <Heading
                  text={`No ${segment === AppointmentsSegment.UPCOMING ? 'upcoming' : 'past'} appointments found for ${formatPersonName({ person })}`}
                  value={3}
                />

                <div className="mt-6"></div>
              </div>
            ) : (
              <div role="list" className="flex flex-col gap-2 w-full">
                {appointmentsResponse?.data?.results.map((appt: AppointmentAttendance) => (
                  <AppointmentAttendanceCard
                    key={appt.id}
                    attendance={appt}
                    personTimeZone={person.time_zone || defaultTimeZone}
                    showEncounterLink={canViewEncounters}
                  />
                ))}
              </div>
            )}

            <div className="mt-4">
              <Paginator
                currentPage={currentPage}
                goToPage={goToPage}
                nextPage={nextPage}
                prevPage={prevPage}
                totalPages={totalPages}
              />
            </div>
          </>
        );
      case AppointmentsSegment.INSIGHTS:
        return <AppointmentAttendanceInsights />;
      default:
        return null;
    }
  };

  const activeButtonColor = Color.BLUE;

  return (
    <Container type="page">
      <div className="">
        {person.most_recent_attendance_feedback && (
          <FeedbackCard
            feedback={person.most_recent_attendance_feedback}
            personId={person.id}
            title="Most Recent Feedback"
          />
        )}
        <div
          className={`border-b pb-1 border-gray-300 mb-2 w-full pt-1 z-19 bg-gray-100 ${STICKY_CHROME_CLASS.pageSection}`}>
          <div className="flex flex-wrap-reverse items-center justify-between gap-2 mb-2">
            <div className="flex justify-start gap-2">
              <Button
                className="capitalize"
                size="xs"
                color={segment === AppointmentsSegment.UPCOMING ? activeButtonColor : Color.WHITE}
                onClick={() => handleTabs(AppointmentsSegment.UPCOMING)}>
                {AppointmentsSegment.UPCOMING}
              </Button>
              <Button
                className="capitalize"
                size="xs"
                color={segment === AppointmentsSegment.PAST ? activeButtonColor : Color.WHITE}
                onClick={() => handleTabs(AppointmentsSegment.PAST)}>
                {AppointmentsSegment.PAST}
              </Button>
              <Button
                className="capitalize"
                size="xs"
                color={segment === AppointmentsSegment.INSIGHTS ? activeButtonColor : Color.WHITE}
                onClick={() => handleTabs(AppointmentsSegment.INSIGHTS)}>
                {AppointmentsSegment.INSIGHTS}
              </Button>
            </div>

            <div className="flex items-center gap-2">
              <div className="flex flex-wrap gap-2">
                {external_links.healthie?.url && (
                  <Button
                    color={Color.INDIGO}
                    href={external_links.healthie.url}
                    rel="noreferrer"
                    size="xs"
                    target="_blank">
                    Open in Healthie
                  </Button>
                )}
                {hasPermission({
                  requiredPermission: APPOINTMENTS_BOOKING_UI,
                  permissions: permissions
                }) && (
                  <Button onClick={() => navigateToBooking(person.id)} size="xs">
                    Book an Appointment
                  </Button>
                )}
                {external_links.healthie?.url && <SendScheduleSummaryButton personId={person.id} />}
              </div>
              {renderAppointmentsDropdown()}
            </div>
          </div>

          {[AppointmentsSegment.UPCOMING, AppointmentsSegment.PAST].includes(segment) && (
            <AppointmentsFilterForm activeParams={searchParams} setParams={setSearchParams} />
          )}
        </div>

        {renderSegment()}
      </div>
    </Container>
  );
};
export default memo(PersonAppointments);
